import {
  AlertTriangle,
  Loading01,
  RefreshCw01,
  Trash01,
  XClose,
} from '@untitledui/icons';
import { useCallback, useRef } from 'react';

import type { UploadedFile } from '@/components/files';

import type { UploadErrorCategory } from './uploadErrors';

/**
 * A file that is currently uploading or has failed to upload.
 *
 * The parent (`ConversationInput`) owns the original `File` object so it can
 * retry the request; this component only needs the display metadata.
 */
export interface PendingUpload {
  /** Stable client-side id, used for retry / dismiss correlation. */
  clientId: string;
  /** Display name (typically `file.name`). */
  fileName: string;
  /** Size in bytes. */
  fileSize: number;
  /** Current state of the upload. */
  status: 'uploading' | 'error';
  /** Short label shown next to the filename when `status === 'error'`. */
  errorTitle?: string;
  /** Longer message used as the chip's tooltip / aria-label suffix. */
  errorMessage?: string;
  /** Bucket the error fell into (drives styling on edge cases). */
  errorCategory?: UploadErrorCategory;
}

interface AttachedFilesChipsProps {
  files: UploadedFile[];
  pendingUploads?: PendingUpload[];
  onRemove: (fileId: number) => void;
  onRetry?: (clientId: string) => void;
  onDismissPending?: (clientId: string) => void;
  /**
   * Called after the last chip is removed so the parent can restore keyboard
   * focus to its input. The chips render as a sibling of the editor, so the
   * chip component cannot locate the editor by walking up ancestors.
   */
  onFocusFallback?: () => void;
}

const EMPTY_PENDING: PendingUpload[] = [];

function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }
  if (bytes >= 1024) {
    return (bytes / 1024).toFixed(0) + ' KB';
  }
  return bytes + ' B';
}

interface SuccessChipProps {
  file: UploadedFile;
  onRemove: (fileId: number) => void;
}

function SuccessChip({ file, onRemove }: SuccessChipProps) {
  const handleRemove = useCallback(() => {
    onRemove(file.id);
  }, [file.id, onRemove]);

  return (
    <div className='flex items-center gap-1.5 px-2 py-1 rounded-full bg-[var(--color-bg-secondary)] border border-[var(--color-border-secondary)] text-xs max-w-[200px]'>
      <span className='truncate'>{file.original_filename}</span>
      <span className='text-[var(--color-text-tertiary)] shrink-0'>
        {formatFileSize(file.file_size)}
      </span>
      <button
        type='button'
        onClick={handleRemove}
        aria-label={`Remove ${file.original_filename}`}
        className='shrink-0 p-1.5 hover:bg-[var(--color-bg-secondary_hover)] rounded-full transition-colors cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 outline-focus-ring'
      >
        <Trash01
          className='size-3 text-[var(--color-text-tertiary)]'
          strokeWidth={2}
        />
      </button>
    </div>
  );
}

interface PendingChipProps {
  upload: PendingUpload;
  onDismiss?: (clientId: string) => void;
}

function PendingChip({ upload, onDismiss }: PendingChipProps) {
  const handleDismiss = useCallback(() => {
    onDismiss?.(upload.clientId);
  }, [upload.clientId, onDismiss]);

  const ariaLabel = `Uploading ${upload.fileName}`;

  return (
    <div
      className='flex items-center gap-1.5 px-2 py-1 rounded-full bg-[var(--color-bg-secondary)] border border-[var(--color-border-secondary)] text-xs max-w-[220px]'
      aria-busy='true'
      aria-label={ariaLabel}
    >
      <Loading01
        aria-hidden='true'
        className='size-3 shrink-0 text-[var(--color-text-tertiary)] motion-safe:animate-spin'
      />
      <span className='truncate'>{upload.fileName}</span>
      <span className='text-[var(--color-text-tertiary)] shrink-0'>
        Uploading…
      </span>
      {onDismiss && (
        <button
          type='button'
          onClick={handleDismiss}
          aria-label={`Cancel uploading ${upload.fileName}`}
          className='shrink-0 p-1.5 hover:bg-[var(--color-bg-secondary_hover)] rounded-full transition-colors cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 outline-focus-ring'
        >
          <XClose
            className='size-3 text-[var(--color-text-tertiary)]'
            strokeWidth={2}
          />
        </button>
      )}
    </div>
  );
}

interface ErrorChipProps {
  upload: PendingUpload;
  onRetry?: (clientId: string) => void;
  onDismiss?: (clientId: string) => void;
}

function ErrorChip({ upload, onRetry, onDismiss }: ErrorChipProps) {
  const handleRetry = useCallback(() => {
    onRetry?.(upload.clientId);
  }, [upload.clientId, onRetry]);

  const handleDismiss = useCallback(() => {
    onDismiss?.(upload.clientId);
  }, [upload.clientId, onDismiss]);

  const fullMessage =
    upload.errorMessage || upload.errorTitle || 'Upload failed';
  const ariaLabel = `Upload failed for ${upload.fileName}: ${fullMessage}`;

  return (
    <div
      role='alert'
      aria-label={ariaLabel}
      title={fullMessage}
      className='flex items-center gap-1.5 px-2 py-1 rounded-full bg-[var(--color-error-50)] dark:bg-[var(--color-error-900)]/20 border border-[var(--color-error-200)] dark:border-[var(--color-error-800)] text-xs max-w-[260px] text-[var(--color-error-700)] dark:text-[var(--color-error-300)]'
    >
      <AlertTriangle
        aria-hidden='true'
        className='size-3 shrink-0'
        strokeWidth={2}
      />
      <span className='truncate'>{upload.fileName}</span>
      {upload.errorTitle && (
        <span className='shrink-0 font-medium' aria-hidden='true'>
          · {upload.errorTitle}
        </span>
      )}
      {onRetry && (
        <button
          type='button'
          onClick={handleRetry}
          aria-label={`Retry uploading ${upload.fileName}`}
          className='shrink-0 p-1.5 hover:bg-[var(--color-error-100)] dark:hover:bg-[var(--color-error-800)]/40 rounded-full transition-colors cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 outline-focus-ring'
        >
          <RefreshCw01 className='size-3' strokeWidth={2} />
        </button>
      )}
      {onDismiss && (
        <button
          type='button'
          onClick={handleDismiss}
          aria-label={`Dismiss failed upload of ${upload.fileName}`}
          className='shrink-0 p-1.5 hover:bg-[var(--color-error-100)] dark:hover:bg-[var(--color-error-800)]/40 rounded-full transition-colors cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 outline-focus-ring'
        >
          <XClose className='size-3' strokeWidth={2} />
        </button>
      )}
    </div>
  );
}

export function AttachedFilesChips({
  files,
  pendingUploads = EMPTY_PENDING,
  onRemove,
  onRetry,
  onDismissPending,
  onFocusFallback,
}: AttachedFilesChipsProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  const handleRemoveWithFocus = useCallback(
    (fileId: number) => {
      try {
        onRemove(fileId);
      } catch {
        // onRemove failure means parent state was not updated;
        // skip focus restoration to avoid targeting stale DOM nodes
        return;
      }
      // After React reconciles the removal, move focus to the next chip or
      // delegate to the parent. Chips render as a sibling of the editor, so
      // the chip component cannot reach the editor via DOM ancestor lookup.
      requestAnimationFrame(() => {
        const nextBtn = containerRef.current?.querySelector('button');
        if (nextBtn) {
          (nextBtn as HTMLElement).focus();
        } else {
          onFocusFallback?.();
        }
      });
    },
    [onRemove, onFocusFallback]
  );

  if (files.length === 0 && pendingUploads.length === 0) {
    return null;
  }

  return (
    <div ref={containerRef} className='flex flex-wrap gap-1.5 px-3 pb-2'>
      {pendingUploads.map(upload =>
        upload.status === 'error' ? (
          <ErrorChip
            key={upload.clientId}
            upload={upload}
            onRetry={onRetry}
            onDismiss={onDismissPending}
          />
        ) : (
          <PendingChip
            key={upload.clientId}
            upload={upload}
            onDismiss={onDismissPending}
          />
        )
      )}
      {files.map(file => (
        <SuccessChip
          key={file.id}
          file={file}
          onRemove={handleRemoveWithFocus}
        />
      ))}
    </div>
  );
}
